import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import { BookOpen, Plus, Trash2, Scale, CheckCircle2, AlertTriangle, RefreshCw } from "lucide-react";
import toast from "react-hot-toast";

const API = `${window.location.origin}/api`;

const formatAmount = (n, currency = "IQD") =>
  new Intl.NumberFormat("ar-IQ").format(Math.round(n || 0)) + (currency === "USD" ? " $" : " د.ع");

const VOUCHER_TYPES = {
  multi: { label: "سند متعدد", color: "bg-cyan-500" },
  journal: { label: "سند قيد", color: "bg-indigo-500" },
  tolerance: { label: "سند سماح", color: "bg-amber-500" },
  exchange: { label: "سند تصريف", color: "bg-purple-500" },
};

const emptyLine = () => ({
  account_type: "customer",
  account_name: "",
  side: "debit",
  amount: 0,
  currency: "IQD",
  exchange_rate: 1500,
  notes: "",
});

export default function MultiVouchersPage() {
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    voucher_type: "multi",
    description: "",
    lines: [emptyLine(), emptyLine()],
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/multi-vouchers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVouchers(res.data.vouchers || []);
    } catch (e) {
      toast.error("فشل جلب السندات");
    } finally {
      setLoading(false);
    }
  };

  const totals = useMemo(() => {
    let debit = 0, credit = 0;
    form.lines.forEach((l) => {
      const amt = (l.currency === "USD" ? (l.amount * (l.exchange_rate || 1500)) : l.amount) || 0;
      if (l.side === "debit") debit += amt; else credit += amt;
    });
    return { debit, credit, diff: debit - credit, balanced: Math.abs(debit - credit) < 0.01 };
  }, [form.lines]);

  const updateLine = (idx, patch) => {
    setForm((f) => ({
      ...f,
      lines: f.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)),
    }));
  };

  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, emptyLine()] }));
  const removeLine = (idx) =>
    setForm((f) => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));

  const submit = async () => {
    if (!totals.balanced) {
      toast.error("السند غير متوازن — المدين ≠ الدائن");
      return;
    }
    if (form.lines.some((l) => !l.account_name || l.amount <= 0)) {
      toast.error("كل السطور تحتاج اسم حساب ومبلغ > 0");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/multi-vouchers`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("تم حفظ السند");
      setOpen(false);
      setForm({ voucher_type: "multi", description: "", lines: [emptyLine(), emptyLine()] });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "فشل الحفظ");
    }
  };

  const deleteVoucher = async (id) => {
    if (!window.confirm("حذف السند؟")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/multi-vouchers/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("تم الحذف");
      setVouchers((arr) => arr.filter((x) => x.id !== id));
    } catch (e) {
      toast.error("فشل الحذف");
    }
  };

  return (
    <div className="space-y-6" data-testid="multi-vouchers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-indigo-400" />
            السندات المتعددة
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            قيد متعدد العمليات (مدين/دائن) مع دعم IQD/USD وتوازن القيد
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} className="border-slate-600 text-slate-200">
            <RefreshCw className="w-4 h-4 me-2" />تحديث
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-voucher-btn">
                <Plus className="w-4 h-4 me-2" />سند جديد
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-3xl max-h-[90vh] overflow-y-auto" dir="rtl">
              <DialogHeader><DialogTitle>إنشاء سند متعدد</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-300">نوع السند</Label>
                    <select
                      value={form.voucher_type}
                      onChange={(e) => setForm({ ...form, voucher_type: e.target.value })}
                      className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-white"
                    >
                      {Object.entries(VOUCHER_TYPES).map(([k, v]) => (
                        <option key={k} value={k}>{v.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-slate-300">الوصف</Label>
                    <Input
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                </div>

                {/* Lines */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-slate-300 text-base font-semibold">سطور القيد</Label>
                    <Button size="sm" onClick={addLine} className="bg-emerald-600">
                      <Plus className="w-3.5 h-3.5 me-1" />إضافة سطر
                    </Button>
                  </div>
                  {form.lines.map((line, idx) => (
                    <div key={idx} className="border border-slate-700 rounded-lg p-3 bg-slate-800/40 space-y-2">
                      <div className="grid grid-cols-12 gap-2">
                        <div className="col-span-5">
                          <Input
                            placeholder="اسم الحساب"
                            value={line.account_name}
                            onChange={(e) => updateLine(idx, { account_name: e.target.value })}
                            className="bg-slate-800 border-slate-700 text-sm"
                          />
                        </div>
                        <div className="col-span-2">
                          <select
                            value={line.side}
                            onChange={(e) => updateLine(idx, { side: e.target.value })}
                            className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm"
                          >
                            <option value="debit">مدين</option>
                            <option value="credit">دائن</option>
                          </select>
                        </div>
                        <div className="col-span-3">
                          <Input
                            type="number"
                            placeholder="المبلغ"
                            value={line.amount}
                            onChange={(e) => updateLine(idx, { amount: parseFloat(e.target.value) || 0 })}
                            className="bg-slate-800 border-slate-700 text-sm"
                          />
                        </div>
                        <div className="col-span-1">
                          <select
                            value={line.currency}
                            onChange={(e) => updateLine(idx, { currency: e.target.value })}
                            className="w-full bg-slate-800 border border-slate-700 rounded px-1 py-1.5 text-xs"
                          >
                            <option value="IQD">د.ع</option>
                            <option value="USD">$</option>
                          </select>
                        </div>
                        <div className="col-span-1">
                          <Button size="sm" variant="outline" onClick={() => removeLine(idx)}
                            className="border-rose-500/40 text-rose-400 h-full w-full p-0">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Totals */}
                <div className={`p-3 rounded-lg border ${totals.balanced ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-rose-500/30 bg-rose-500/5'}`}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">
                      {totals.balanced ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <AlertTriangle className="w-4 h-4 text-rose-400" />}
                      <Scale className="w-4 h-4 text-slate-400" />
                      <span className={totals.balanced ? "text-emerald-400" : "text-rose-400"}>
                        {totals.balanced ? "متوازن" : `غير متوازن (فرق: ${formatAmount(totals.diff)})`}
                      </span>
                    </span>
                    <div className="flex gap-4 text-xs">
                      <span>مدين: <strong className="text-white">{formatAmount(totals.debit)}</strong></span>
                      <span>دائن: <strong className="text-white">{formatAmount(totals.credit)}</strong></span>
                    </div>
                  </div>
                </div>

                <Button onClick={submit} disabled={!totals.balanced}
                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                  data-testid="save-voucher-btn">
                  حفظ السند
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* List */}
      <Card className="bg-slate-800/60 border-slate-700">
        <CardHeader><CardTitle className="text-white">السجل ({vouchers.length})</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center text-slate-400 py-12">جاري التحميل...</div>
          ) : vouchers.length === 0 ? (
            <div className="text-center text-slate-400 py-12" data-testid="vouchers-empty">
              <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-40" />
              لا توجد سندات — اضغط "سند جديد"
            </div>
          ) : (
            <div className="space-y-2">
              {vouchers.map((v) => (
                <div key={v.id} className="border border-slate-700 rounded-lg p-4 hover:bg-slate-700/30">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <Badge className={`${VOUCHER_TYPES[v.voucher_type]?.color || 'bg-slate-500'} text-white`}>
                          {VOUCHER_TYPES[v.voucher_type]?.label || v.voucher_type}
                        </Badge>
                        <span className="text-sm text-slate-300">{v.description || "بدون وصف"}</span>
                      </div>
                      <div className="text-xs text-slate-500">
                        {new Date(v.date).toLocaleString("ar-IQ")} • {v.lines?.length || 0} سطر •
                        مدين: {formatAmount(v.totals?.total_debit)} • دائن: {formatAmount(v.totals?.total_credit)}
                      </div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => deleteVoucher(v.id)}
                      className="border-rose-500/40 text-rose-400 hover:bg-rose-500/10">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
